import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import Logo from "./Logo";
import { BRAND } from "../data/siteData";

const LINKS = [
  { to: "/", label: "Home", testid: "nav-link-home" },
  { to: "/services", label: "Services", testid: "nav-link-services" },
  { to: "/about", label: "About", testid: "nav-link-about" },
  { to: "/gallery", label: "Gallery", testid: "nav-link-gallery" },
  { to: "/booking", label: "Book a Quote", testid: "nav-link-booking" },
  { to: "/contact", label: "Contact", testid: "nav-link-contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);

  return (
    <header className="fixed inset-x-0 top-0 z-40 border-b border-[#234E33]/10 bg-[#FBF9F5]/85 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 md:h-20 lg:px-8">
        <Link to="/" data-testid="nav-brand-logo" className="flex items-center gap-3" onClick={() => setOpen(false)}>
          <Logo className="h-9 w-9" />
          <span className="font-display text-xl font-black tracking-tight text-[#1A2E20]">SKLOCCOM</span>
        </Link>

        <nav className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={({ isActive }) =>
                `text-sm font-medium tracking-wide transition-colors duration-200 ${
                  isActive ? "text-[#2E6941]" : "text-[#1A2E20]/70 hover:text-[#1A2E20]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:block">
          <a
            href={`tel:${BRAND.phone}`}
            data-testid="nav-phone-cta"
            className="inline-flex items-center gap-2 rounded-full bg-[#1A2E20] px-5 py-2.5 text-sm font-semibold text-[#FBF9F5] transition-colors duration-200 hover:bg-[#2E6941]"
          >
            <Phone size={15} />
            {BRAND.phoneDisplay}
          </a>
        </div>

        <button
          className="text-[#1A2E20] lg:hidden"
          data-testid="nav-mobile-menu-btn"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden border-t border-[#234E33]/10 bg-[#FBF9F5] lg:hidden"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-${l.testid}`}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `rounded-md px-3 py-3 text-base font-medium ${
                      isActive ? "bg-[#2E6941]/10 text-[#2E6941]" : "text-[#1A2E20]/80"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <a
                href={`tel:${BRAND.phone}`}
                data-testid="mobile-nav-phone-cta"
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-[#1A2E20] px-5 py-3 text-sm font-semibold text-[#FBF9F5]"
              >
                <Phone size={15} />
                Call {BRAND.phoneDisplay}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
